/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { typeStyle } from '@carvana/showroom-css-theme';
import { useIntersectionObserver } from 'usehooks-ts';
import { Warranty } from '@components/service-and-repair/constants';
import styles from './vehicle-coverage.module.css';
import { DetailToggle } from '../shared/detail-toggle/detail-toggle';
import { WarrantyData } from './vehicle-coverage';
import { WarrantyHighlightProps, ExpiredLabel, WarrantyHighlight } from './shared';

export type ManufacturerWarrantyProps = {
  warranties?: WarrantyData[];
  onIntersection?: () => void;
  onToggle?: (isOpen: boolean) => void;
};

export const ManufacturerWarranty = ({ warranties, onIntersection, onToggle }: ManufacturerWarrantyProps) => {
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (isIntersecting && onIntersection) onIntersection();
    },
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const basicCoverage = warranties?.find((w) => w.type === Warranty.OEMBasic);
  const powertrainCoverage = warranties?.find((w) => w.type === Warranty.OEMPowerTrain);
  const isBasicCoverageExpired = basicCoverage?.isExpired || basicCoverage === undefined;
  const isPowertrainCoverageExpired = powertrainCoverage?.isExpired || powertrainCoverage === undefined;
  const isManufacturerWarrantyExpired = isBasicCoverageExpired && isPowertrainCoverageExpired;

  const shouldBasicShowExpired = isBasicCoverageExpired && !isManufacturerWarrantyExpired;
  const basicDurationToShow = isBasicCoverageExpired && !shouldBasicShowExpired ? '' : basicCoverage?.durationLeft;
  const shouldPowerTrainShowExpired = isPowertrainCoverageExpired && !isManufacturerWarrantyExpired;
  const powerTrainDurationToShow =
    isPowertrainCoverageExpired && !shouldPowerTrainShowExpired ? '' : powertrainCoverage?.durationLeft;

  return (
    <>
      <DetailToggle
        id="manufacturer-warranty"
        onDetailsToggle={(val) => {
          setIsExpanded(val);
          return onToggle && onToggle(val);
        }}
        header={
          <span ref={ref}>
            Manufacturer Warranty
            {isManufacturerWarrantyExpired && (
              <span className={styles['vehicle-coverage-expired']} style={typeStyle('label-xs')}>
                Expired
              </span>
            )}
          </span>
        }
        headerContent={
          <div className={styles['vehicle-coverage-content']}>
            <div className={styles['warranty-text']} style={typeStyle('body-s')}>
              Your vehicle comes with a manufacturer&apos;s warranty, covering basic and powertrain repairs.
            </div>

            {!isExpanded && (
              <div className={styles['collapsed-warranties']}>
                <BasicCoverageWarranty
                  isExpired={shouldBasicShowExpired}
                  durationLeft={basicDurationToShow}
                  totalCoverageInMiles={basicCoverage?.totalCoverageInMiles}
                  id="basic-coverage"
                />

                <PowertrainCoverageWarranty
                  isExpired={shouldPowerTrainShowExpired}
                  durationLeft={powerTrainDurationToShow}
                  totalCoverageInMiles={powertrainCoverage?.totalCoverageInMiles}
                  id="powertrain-coverage"
                />
              </div>
            )}
          </div>
        }
      >
        <BasicCoverageDetails
          isExpired={shouldBasicShowExpired}
          durationLeft={basicDurationToShow}
          totalCoverageInMiles={basicCoverage?.totalCoverageInMiles}
          id="basic-coverage"
        />

        <PowertrainCoverageDetails
          isExpired={shouldPowerTrainShowExpired}
          durationLeft={powerTrainDurationToShow}
          totalCoverageInMiles={powertrainCoverage?.totalCoverageInMiles}
          id="powertrain-coverage"
        />
      </DetailToggle>
    </>
  );
};

const BasicCoverageWarranty = ({ isExpired, durationLeft, totalCoverageInMiles, id }: WarrantyHighlightProps) => {
  return (
    <div className={styles['coverage']} style={typeStyle('body-s')} data-testid={id}>
      <div>
        <span style={typeStyle('header-xs')}>Basic coverage</span>
        <ExpiredLabel isExpired={isExpired} />
      </div>

      <WarrantyHighlight
        isExpired={isExpired}
        durationLeft={durationLeft}
        totalCoverageInMiles={totalCoverageInMiles}
        id={id}
      />
    </div>
  );
};

const PowertrainCoverageWarranty = ({ isExpired, durationLeft, totalCoverageInMiles, id }: WarrantyHighlightProps) => {
  return (
    <div className={styles['coverage']} style={typeStyle('body-s')} data-testid={id}>
      <div>
        <span style={typeStyle('header-xs')}>Powertrain coverage</span>
        <ExpiredLabel isExpired={isExpired} />
      </div>

      <WarrantyHighlight
        isExpired={isExpired}
        durationLeft={durationLeft}
        totalCoverageInMiles={totalCoverageInMiles}
        id={id}
      />
    </div>
  );
};

const BasicCoverageDetails = ({ isExpired, durationLeft, totalCoverageInMiles, id }: WarrantyHighlightProps) => {
  return (
    <div className={styles['expanded-coverage']} data-testid="basic-coverage-details">
      <BasicCoverageWarranty
        isExpired={isExpired}
        durationLeft={durationLeft}
        totalCoverageInMiles={totalCoverageInMiles}
        id={id}
      />

      <p style={typeStyle('body-s')}>
        Your basic warranty{' '}
        <span style={typeStyle('header-xs')}>covers repair costs due to factory defects or errors</span>
      </p>

      <div style={typeStyle('body-s')}>
        <p>
          This warranty <span style={typeStyle('header-xs')}>doesn&apos;t cover:</span>
        </p>
        <ul>
          <li>Routine maintenance, like worn tires or brakes</li>
          <li>Cosmetic or accident damage</li>
          <li>Failures resulting from influence or lack of maintenance</li>
        </ul>
      </div>
    </div>
  );
};

const PowertrainCoverageDetails = ({ isExpired, durationLeft, totalCoverageInMiles, id }: WarrantyHighlightProps) => {
  return (
    <div className={styles['expanded-coverage']} data-testid="powertrain-coverage-details">
      <PowertrainCoverageWarranty
        isExpired={isExpired}
        durationLeft={durationLeft}
        totalCoverageInMiles={totalCoverageInMiles}
        id={id}
      />

      <p style={typeStyle('body-s')}>
        The powertrain warranty is also included in the manufacturer warranty and covers the{' '}
        <span style={typeStyle('header-xs')}>parts of your vehicle that provide power and make it move</span>.
        Components that are commonly covered in the powertrain warranty include the{' '}
        <span style={typeStyle('header-xs')}>engine, transmission</span>, and related parts.
      </p>

      <div style={typeStyle('body-s')}>
        <p>
          This warranty <span style={typeStyle('header-xs')}>doesn&apos;t cover</span>:
        </p>
        <ul>
          <li>Routine maintenance, like worn tires or brakes</li>
          <li>Cosmetic or accident damage</li>
          <li>Failures resulting from influence or lack of maintenance</li>
        </ul>
      </div>
    </div>
  );
};
